import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Sparkles, CheckCircle2, Clock, AlertTriangle, Lightbulb } from 'lucide-react'
import { api, extractApiError, isPlanLimitError, isSessionEndedError } from '../lib/api'
import { goalsAnalysisResponse } from '../lib/types'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'

const STATUS_LABEL = { on_track: 'On track', partial: 'Partial', attention: 'Needs attention' }
const STATUS_PILL = {
  on_track: 'bg-[color:var(--color-status-complete,#16a34a)]/15 text-[color:var(--color-status-complete,#16a34a)]',
  partial: 'bg-[color:var(--color-priority-high-bg,#fef3c7)] text-[color:var(--color-priority-high)]',
  attention: 'bg-[color:var(--color-priority-urgent-bg,#fee2e2)] text-[color:var(--color-priority-urgent)]',
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-2.5 text-center">
      <div className="text-lg font-bold tabular-nums text-[color:var(--color-ink-strong)]">{value}</div>
      <div className="text-[11px] text-[color:var(--color-ink-muted)]">{label}</div>
    </div>
  )
}

function List({ icon: Icon, title, items, empty }) {
  return (
    <div>
      <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-subtle)]">
        <Icon size={13} /> {title}
      </h4>
      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">{empty}</p>
      ) : (
        <ul className="space-y-1 text-sm text-[color:var(--color-ink)]">
          {items.map((it, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[color:var(--color-ink-subtle)]" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Analysis for the viewed period + type. Loads the deterministic facts on open;
 * a button generates the AI narrative (facts stay deterministic — the model only
 * phrases summary/insights/recommendations/next goals). Works for weekly AND
 * monthly (the API keys off `type`).
 */
export function GoalAnalysisModal({ open, onClose, type, anchor, scope = 'involved', periodLabel }) {
  const key = ['goals-analysis', type, scope, anchor ?? 'current']
  const query = useQuery({
    queryKey: key,
    enabled: open,
    queryFn: async () => {
      // Analyse the SAME set the board shows (All/Mine) so the numbers match.
      const qs = new URLSearchParams({ type, scope })
      if (anchor) qs.set('anchor', anchor)
      return goalsAnalysisResponse.parse((await api.get(`/goals/analysis?${qs.toString()}`)).data)
    },
  })

  const generate = useMutation({
    mutationFn: async () =>
      goalsAnalysisResponse.parse(
        (await api.post('/goals/analysis/generate', { type, scope, anchor: anchor ?? undefined })).data,
      ),
    onError: (err) => {
      // The response interceptor already surfaces a single upgrade toast for
      // plan-limit hits (and its own toast for a dead session) — skip our
      // generic one so the user doesn't see the SAME message twice.
      if (isPlanLimitError(err) || isSessionEndedError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  // Prefer the freshly-generated (AI) result once it exists.
  const a = generate.data ?? query.data
  const g = a?.goalSummary ?? {}
  const t = a?.taskSummary ?? {}
  const hasAi = Boolean(a?.summary) || (a?.insights?.length ?? 0) > 0

  return (
    <Modal open={open} onClose={onClose} size="lg" title={`Analysis — ${periodLabel ?? ''}`}>
      {query.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Analysing your goals…</p>
      ) : query.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">{extractApiError(query.error).message}</p>
      ) : a ? (
        <div className="space-y-5">
          {/* Overall status + average progress */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-[color:var(--color-ink-muted)]">Overall:</span>
            <span className={'rounded-full px-2 py-0.5 text-xs font-semibold ' + (STATUS_PILL[a.progress] ?? 'bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)]')}>
              {STATUS_LABEL[a.progress] ?? '—'}
            </span>
            <span className="text-sm text-[color:var(--color-ink-muted)]">
              · {a.averageProgress ?? 0}% average progress
            </span>
          </div>

          {/* Goals + actual work stats */}
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-subtle)]">Goals</h4>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              <Stat label="Total" value={g.total ?? 0} />
              <Stat label="Completed" value={g.completed ?? 0} />
              <Stat label="In progress" value={g.inProgress ?? 0} />
              <Stat label="Not started" value={g.notStarted ?? 0} />
              <Stat label="On hold" value={g.onHold ?? 0} />
            </div>
          </div>
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-ink-subtle)]">Actual work</h4>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              <Stat label="Tasks done" value={t.tasksCompleted ?? 0} />
              <Stat label="Worked on" value={t.tasksTouched ?? 0} />
              <Stat label="Meetings" value={t.meetings ?? 0} />
              <Stat label="Hours" value={t.hoursLogged ?? 0} />
              <Stat label="Days" value={t.daysReported ?? 0} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <List icon={CheckCircle2} title="Achievements" items={a.achievements ?? []} empty="Nothing completed yet." />
            <List icon={Clock} title="Unfinished" items={(a.pending ?? []).map((p) => p.name)} empty="Everything is on track." />
          </div>
          {(a.blockers ?? []).length > 0 ? (
            <List icon={AlertTriangle} title="Blockers" items={a.blockers.map((b) => b.blocker)} empty="" />
          ) : null}

          {/* AI narrative */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4">
            {hasAi ? (
              <div className="space-y-3">
                {a.summary ? (
                  <p className="text-sm text-[color:var(--color-ink)]">{a.summary}</p>
                ) : null}
                {(a.insights ?? []).length > 0 ? (
                  <List icon={Lightbulb} title="Insights" items={a.insights} empty="" />
                ) : null}
                {(a.recommendations ?? []).length > 0 ? (
                  <List icon={Sparkles} title="Recommendations" items={a.recommendations} empty="" />
                ) : null}
                {(a.suggestedNextGoals ?? []).length > 0 ? (
                  <List icon={CheckCircle2} title="Suggested next goals" items={a.suggestedNextGoals} empty="" />
                ) : null}
                <p className="text-[11px] text-[color:var(--color-ink-subtle)]">
                  AI phrases the narrative from your real data — facts are computed, never invented.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm text-[color:var(--color-ink-muted)]">
                  Generate an AI summary, insights and recommendations from the facts above.
                </p>
                <Button onClick={() => generate.mutate()} loading={generate.isPending}>
                  <Sparkles size={15} /> Generate AI Insights
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
